import { IconPlugConnected, IconPlus, IconRefresh } from '@tabler/icons-react'
import { useState } from 'react'
import { Link } from 'react-router'

import { CanWrite, ViewerNotice } from '../components/CanWrite'
import { CopyButton } from '../components/CopyButton'
import { Dialog } from '../components/Dialog'
import {
  Alert,
  Badge,
  EmptyState,
  ErrorState,
  FieldErrorText,
  LoadingState,
} from '../components/States'
import { JsonDisclosure } from '../components/JsonDisclosure'
import type { CredentialView } from '../lib/apiTypes'
import { formatDateWib } from '../lib/format'
import { describeError, hasFieldError } from '../lib/describeError'
import {
  credentialSettingsHint,
  credentialSettingsTemplate,
  providerBJBVA,
  providerBJBQRIS,
  providerSandbox,
} from '../lib/providerSettings'
import {
  useCreateCredential,
  useCredentials,
  useSetCredentialSecrets,
  useTestCredential,
  useUpdateCredential,
} from '../lib/hooks'
import { rowStatusLabel, rowStatusTone } from '../lib/status'

/**
 * Kredensial provider: client key, secret write-only, dan dua URL inbound yang
 * harus didaftarkan ke bank. Secret tidak pernah ditampilkan kembali oleh server,
 * jadi form pemasangan secret selalu kosong dan hanya mengirim nilai baru.
 */
export function CredentialsPage() {
  const credentials = useCredentials()
  const [creating, setCreating] = useState(false)
  const [editing, setEditing] = useState<CredentialView | null>(null)
  const [feedback, setFeedback] = useState<{ tone: 'success' | 'danger'; text: string } | null>(null)

  return (
    <>
      <div className="page-head">
        <div className="page-head__text">
          <h1>Kredensial</h1>
          <p className="muted">
            Satu kredensial mewakili satu akun di sisi bank. Isinya client key, materi
            rahasia yang hanya bisa ditulis, dan pengaturan tanda tangan yang bisa
            disesuaikan tanpa mengubah kode.
          </p>
        </div>
        <CanWrite>
          <button type="button" className="btn btn--primary" onClick={() => setCreating(true)}>
            <IconPlus size={18} stroke={1.75} aria-hidden="true" />
            Buat kredensial
          </button>
        </CanWrite>
      </div>

      {feedback ? <Alert tone={feedback.tone}>{feedback.text}</Alert> : null}

      {credentials.isPending ? (
        <LoadingState what="daftar kredensial" />
      ) : credentials.isError ? (
        <ErrorState
          title="Gagal memuat daftar kredensial"
          error={credentials.error}
          onRetry={() => void credentials.refetch()}
          retrying={credentials.isFetching}
        />
      ) : (credentials.data?.data.length ?? 0) === 0 ? (
        <EmptyState
          title="Belum ada kredensial"
          body="Kredensial dibuat setelah akun bank terbit. Tanpa kredensial, merchant tidak bisa menerbitkan QRIS maupun nomor Virtual Account."
        />
      ) : (
        <div className="rows">
          {credentials.data?.data.map((row) => (
            <article className="row-item" key={row.id}>
              <div className="row-item__main">
                <span className="row-item__title mono">{row.slug}</span>
                <span className="row-item__meta">
                  {row.provider} di lingkungan {row.environment}, dipakai {row.merchant_count}{' '}
                  merchant
                </span>
                <span className="row-item__meta">
                  Client key {row.client_key || 'belum diisi'}
                  {`, client secret ${row.client_secret_set ? 'terpasang' : 'belum ada'}`}
                  {`, private key ${row.private_key_set ? 'terpasang' : 'belum ada'}`}
                  {`, public key bank ${row.bank_public_key_set ? 'terpasang' : 'belum ada'}`}
                  {`, notify secret ${row.notify_secret_set ? 'terpasang' : 'belum ada'}`}
                </span>
                <span className="row-item__meta">Dibuat {formatDateWib(row.created_at)}</span>
              </div>
              <div className="state__actions">
                <Badge tone={rowStatusTone(row.status)}>{rowStatusLabel(row.status)}</Badge>
                <CanWrite>
                  <button type="button" className="btn btn--sm" onClick={() => setEditing(row)}>
                    Ubah
                  </button>
                </CanWrite>
              </div>
            </article>
          ))}
        </div>
      )}

      {!credentials.isPending && !credentials.isError ? <ViewerNotice /> : null}

      {creating ? (
        <CreateCredentialDialog
          onClose={() => setCreating(false)}
          onDone={(message) => {
            setCreating(false)
            setFeedback({ tone: 'success', text: message })
          }}
        />
      ) : null}

      {editing ? (
        <EditCredentialDialog
          credential={editing}
          onClose={() => setEditing(null)}
          onFeedback={setFeedback}
        />
      ) : null}
    </>
  )
}

function CreateCredentialDialog({
  onClose,
  onDone,
}: {
  onClose: () => void
  onDone: (message: string) => void
}) {
  const create = useCreateCredential()
  const [slug, setSlug] = useState('')
  const [provider, setProvider] = useState(providerBJBQRIS)
  const [environment, setEnvironment] = useState('sandbox')
  const [clientKey, setClientKey] = useState('')
  const [clientSecret, setClientSecret] = useState('')
  const [privateKey, setPrivateKey] = useState('')
  const [bankPublicKey, setBankPublicKey] = useState('')
  const [notifySecret, setNotifySecret] = useState('')
  const [settings, setSettings] = useState(credentialSettingsTemplate(providerBJBQRIS))
  const [settingsTouched, setSettingsTouched] = useState(false)
  const [settingsError, setSettingsError] = useState('')

  const isSandboxProvider = provider === providerSandbox

  function changeProvider(next: string) {
    setProvider(next)
    // Contoh settings berbeda per kanal dan server menolak kunci yang tidak
    // dikenal, jadi isian yang belum disentuh operator ikut berganti.
    if (!settingsTouched) {
      setSettings(credentialSettingsTemplate(next))
    }
  }

  function submit() {
    setSettingsError('')
    let parsed: unknown = {}
    if (settings.trim()) {
      try {
        parsed = JSON.parse(settings)
      } catch {
        setSettingsError('Isi settings bukan JSON yang sah.')
        return
      }
    }
    create.mutate(
      {
        slug: slug.trim(),
        provider,
        environment,
        client_key: clientKey.trim(),
        client_secret: isSandboxProvider ? '' : clientSecret,
        private_key_pem: isSandboxProvider ? '' : privateKey,
        bank_public_key_pem: isSandboxProvider ? '' : bankPublicKey,
        notify_secret: isSandboxProvider ? '' : notifySecret,
        settings: parsed,
      },
      {
        onSuccess: (row) =>
          onDone(
            `Kredensial ${row.slug} dibuat. Pasang materi rahasianya di sisi bank bila belum, lalu jalankan uji koneksi.`,
          ),
      },
    )
  }

  return (
    <Dialog
      title="Buat kredensial"
      onClose={onClose}
      wide
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Batal
          </button>
          <button
            type="button"
            className="btn btn--primary"
            onClick={submit}
            disabled={create.isPending || slug.trim() === ''}
          >
            {create.isPending ? 'Menyimpan...' : 'Simpan kredensial'}
          </button>
        </>
      }
    >
      {create.error ? <Alert tone="danger">{describeError(create.error)}</Alert> : null}

      <div className="form-grid">
        <div className="field">
          <label className="field__label" htmlFor="cr-slug">
            Slug
          </label>
          <input
            id="cr-slug"
            className="input mono"
            value={slug}
            onChange={(event) => setSlug(event.target.value)}
            aria-invalid={hasFieldError(create.error, 'slug')}
          />
          <FieldErrorText error={create.error} field="slug" />
        </div>

        <div className="field">
          <label className="field__label" htmlFor="cr-provider">
            Provider
          </label>
          <select
            id="cr-provider"
            className="select"
            value={provider}
            onChange={(event) => changeProvider(event.target.value)}
          >
            <option value={providerBJBQRIS}>bjb-qris (SNAP BI, QRIS MPM)</option>
            <option value={providerBJBVA}>bjb-va (Virtual Account On Us)</option>
            <option value={providerSandbox}>sandbox (tanpa bank)</option>
          </select>
          <span className="field__hint">
            Provider sandbox tidak memakai tanda tangan maupun materi rahasia. QRIS
            dan Virtual Account memakai kredensial dan path bank yang berbeda.
          </span>
        </div>

        <div className="field">
          <label className="field__label" htmlFor="cr-environment">
            Lingkungan
          </label>
          <select
            id="cr-environment"
            className="select"
            value={environment}
            onChange={(event) => setEnvironment(event.target.value)}
          >
            <option value="sandbox">sandbox</option>
            <option value="production">production</option>
          </select>
        </div>

        <div className="field">
          <label className="field__label" htmlFor="cr-client-key">
            Client key
          </label>
          <input
            id="cr-client-key"
            className="input mono"
            value={clientKey}
            onChange={(event) => setClientKey(event.target.value)}
            aria-invalid={hasFieldError(create.error, 'client_key')}
          />
          <span className="field__hint">
            Nilai ini bukan rahasia dan tampil di daftar, dipakai bank untuk mengenali
            TAHU Pay.
          </span>
          <FieldErrorText error={create.error} field="client_key" />
        </div>

        {!isSandboxProvider ? (
          <>
            <div className="field form-grid--full">
              <label className="field__label" htmlFor="cr-client-secret">
                Client secret
              </label>
              <input
                id="cr-client-secret"
                className="input"
                type="password"
                autoComplete="new-password"
                value={clientSecret}
                onChange={(event) => setClientSecret(event.target.value)}
              />
              <span className="field__hint">
                Bersifat hanya tulis. Setelah disimpan, server tidak pernah menampilkannya
                lagi; mengganti berarti mengirim nilai baru.
              </span>
              <FieldErrorText error={create.error} field="client_secret" />
            </div>

            <div className="field form-grid--full">
              <label className="field__label" htmlFor="cr-private-key">
                Private key TAHU Pay (PEM)
              </label>
              <textarea
                id="cr-private-key"
                className="textarea mono"
                value={privateKey}
                onChange={(event) => setPrivateKey(event.target.value)}
              />
              <FieldErrorText error={create.error} field="private_key_pem" />
            </div>

            <div className="field form-grid--full">
              <label className="field__label" htmlFor="cr-bank-key">
                Public key bank (PEM)
              </label>
              <textarea
                id="cr-bank-key"
                className="textarea mono"
                value={bankPublicKey}
                onChange={(event) => setBankPublicKey(event.target.value)}
              />
              <FieldErrorText error={create.error} field="bank_public_key_pem" />
            </div>

            <div className="field form-grid--full">
              <label className="field__label" htmlFor="cr-notify-secret">
                Notify secret
              </label>
              <input
                id="cr-notify-secret"
                className="input"
                type="password"
                autoComplete="new-password"
                value={notifySecret}
                onChange={(event) => setNotifySecret(event.target.value)}
              />
              <span className="field__hint">
                Dipakai memverifikasi tanda tangan notifikasi dari bank. Kosongkan bila
                bank memakai client secret.
              </span>
              <FieldErrorText error={create.error} field="notify_secret" />
            </div>
          </>
        ) : null}

        <div className="field form-grid--full">
          <label className="field__label" htmlFor="cr-settings">
            Settings JSON
          </label>
          <textarea
            id="cr-settings"
            className="textarea"
            value={settings}
            onChange={(event) => {
              setSettingsTouched(true)
              setSettings(event.target.value)
            }}
            aria-describedby="cr-settings-hint"
            aria-invalid={Boolean(settingsError) || hasFieldError(create.error, 'settings')}
          />
          <span className="field__hint" id="cr-settings-hint">
            {credentialSettingsHint(provider)}
          </span>
          {settingsError ? <p className="field__error">{settingsError}</p> : null}
          <FieldErrorText error={create.error} field="settings" />
        </div>
      </div>
    </Dialog>
  )
}

/**
 * Ubah kredensial: status, client key, settings, dan pemasangan materi rahasia
 * baru. Form secret selalu kosong karena server tidak pernah mengirim nilai lama.
 */
function EditCredentialDialog({
  credential,
  onClose,
  onFeedback,
}: {
  credential: CredentialView
  onClose: () => void
  onFeedback: (value: { tone: 'success' | 'danger'; text: string }) => void
}) {
  const update = useUpdateCredential(credential.id)
  const secrets = useSetCredentialSecrets(credential.id)
  const test = useTestCredential(credential.id)
  const [status, setStatus] = useState(credential.status)
  const [clientKey, setClientKey] = useState(credential.client_key)
  const [settings, setSettings] = useState(
    credential.settings ? JSON.stringify(credential.settings, null, 2) : '',
  )
  const [clientSecret, setClientSecret] = useState('')
  const [privateKey, setPrivateKey] = useState('')
  const [bankPublicKey, setBankPublicKey] = useState('')
  const [notifySecret, setNotifySecret] = useState('')
  const [localError, setLocalError] = useState('')

  const isSandbox = credential.provider === providerSandbox
  const busy = update.isPending || secrets.isPending

  function save() {
    setLocalError('')
    let parsed: unknown = undefined
    if (settings.trim()) {
      try {
        parsed = JSON.parse(settings)
      } catch {
        setLocalError('Isi settings bukan JSON yang sah.')
        return
      }
    }

    update.mutate(
      {
        status: status as CredentialView['status'],
        client_key: clientKey.trim(),
        ...(parsed === undefined ? {} : { settings: parsed }),
      },
      {
        onSuccess: () => {
          const secretPayload = {
            ...(clientSecret ? { client_secret: clientSecret } : {}),
            ...(privateKey ? { private_key_pem: privateKey } : {}),
            ...(bankPublicKey ? { bank_public_key_pem: bankPublicKey } : {}),
            ...(notifySecret ? { notify_secret: notifySecret } : {}),
          }
          if (Object.keys(secretPayload).length === 0) {
            onFeedback({ tone: 'success', text: 'Perubahan kredensial disimpan.' })
            onClose()
            return
          }
          secrets.mutate(secretPayload, {
            onSuccess: () => {
              setClientSecret('')
              setPrivateKey('')
              setBankPublicKey('')
              setNotifySecret('')
              onFeedback({
                tone: 'success',
                text: 'Perubahan dan materi rahasia baru disimpan. Jalankan uji koneksi untuk memastikan bank menerimanya.',
              })
              onClose()
            },
            onError: (error) => onFeedback({ tone: 'danger', text: describeError(error) }),
          })
        },
        onError: (error) => onFeedback({ tone: 'danger', text: describeError(error) }),
      },
    )
  }

  const error = update.error ?? secrets.error

  return (
    <Dialog
      title={`Ubah kredensial ${credential.slug}`}
      onClose={onClose}
      wide
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Batal
          </button>
          <button
            type="button"
            className="btn btn--primary"
            onClick={save}
            disabled={busy}
          >
            {busy ? 'Menyimpan...' : 'Simpan perubahan'}
          </button>
        </>
      }
    >
      {error ? <Alert tone="danger">{describeError(error)}</Alert> : null}

      <div className="form-grid">
        <div className="field">
          <label className="field__label" htmlFor="ce-status">
            Status
          </label>
          <select
            id="ce-status"
            className="select"
            value={status}
            onChange={(event) => setStatus(event.target.value as CredentialView['status'])}
          >
            <option value="ACTIVE">Aktif</option>
            <option value="DISABLED">Nonaktif</option>
          </select>
          <span className="field__hint">
            Kredensial nonaktif menolak transaksi baru tanpa menghapus riwayatnya.
          </span>
        </div>

        <div className="field">
          <label className="field__label" htmlFor="ce-client-key">
            Client key
          </label>
          <input
            id="ce-client-key"
            className="input mono"
            value={clientKey}
            onChange={(event) => setClientKey(event.target.value)}
            aria-invalid={hasFieldError(update.error, 'client_key')}
          />
          <FieldErrorText error={update.error} field="client_key" />
        </div>
      </div>

      {!isSandbox ? (
        <>
          <div className="field">
            <label className="field__label" htmlFor="ce-client-secret">
              Client secret baru
            </label>
            <input
              id="ce-client-secret"
              className="input"
              type="password"
              autoComplete="new-password"
              placeholder={
                credential.client_secret_set ? 'terpasang, kosongkan bila tidak diganti' : 'belum ada'
              }
              value={clientSecret}
              onChange={(event) => setClientSecret(event.target.value)}
            />
            <FieldErrorText error={secrets.error} field="client_secret" />
          </div>

          <div className="field">
            <label className="field__label" htmlFor="ce-private-key">
              Private key baru (PEM)
            </label>
            <textarea
              id="ce-private-key"
              className="textarea mono"
              placeholder={
                credential.private_key_set ? 'terpasang, kosongkan bila tidak diganti' : 'belum ada'
              }
              value={privateKey}
              onChange={(event) => setPrivateKey(event.target.value)}
            />
            <FieldErrorText error={secrets.error} field="private_key_pem" />
          </div>

          <div className="field">
            <label className="field__label" htmlFor="ce-bank-key">
              Public key bank baru (PEM)
            </label>
            <textarea
              id="ce-bank-key"
              className="textarea mono"
              placeholder={
                credential.bank_public_key_set
                  ? 'terpasang, kosongkan bila tidak diganti'
                  : 'belum ada'
              }
              value={bankPublicKey}
              onChange={(event) => setBankPublicKey(event.target.value)}
            />
            <FieldErrorText error={secrets.error} field="bank_public_key_pem" />
          </div>

          <div className="field">
            <label className="field__label" htmlFor="ce-notify-secret">
              Notify secret baru
            </label>
            <input
              id="ce-notify-secret"
              className="input"
              type="password"
              autoComplete="new-password"
              placeholder={
                credential.notify_secret_set ? 'terpasang, kosongkan bila tidak diganti' : 'belum ada'
              }
              value={notifySecret}
              onChange={(event) => setNotifySecret(event.target.value)}
            />
            <FieldErrorText error={secrets.error} field="notify_secret" />
          </div>
        </>
      ) : null}

      <div className="field">
        <label className="field__label" htmlFor="ce-settings">
          Settings JSON
        </label>
        <textarea
          id="ce-settings"
          className="textarea"
          value={settings}
          onChange={(event) => setSettings(event.target.value)}
          aria-describedby="ce-settings-hint"
          aria-invalid={Boolean(localError) || hasFieldError(update.error, 'settings')}
        />
        <span className="field__hint" id="ce-settings-hint">
          {credentialSettingsHint(credential.provider)}
        </span>
        {localError ? <p className="field__error">{localError}</p> : null}
        <FieldErrorText error={update.error} field="settings" />
        <JsonDisclosure summary="Lihat settings tersimpan" value={credential.settings} />
      </div>

      <div className="field">
        <span className="field__label">URL inbound untuk didaftarkan ke bank</span>
        {credential.inbound_token_url || credential.inbound_notify_url ? (
          <>
            <InboundUrl label="URL access token" value={credential.inbound_token_url} />
            <InboundUrl label="URL notifikasi" value={credential.inbound_notify_url} />
          </>
        ) : (
          <p className="muted">
            URL belum tersedia. Server menyusunnya dari alamat publik inbound, dan untuk
            provider sandbox memang tidak ada.
          </p>
        )}
      </div>

      <div className="field">
        <span className="field__label">Uji koneksi</span>
        <p className="muted">
          Uji koneksi meminta access token ke bank memakai materi rahasia yang tersimpan.
          Pesan kegagalan berasal dari bank, tanpa membocorkan secret.
        </p>
        <div className="state__actions">
          <button
            type="button"
            className="btn"
            disabled={test.isPending}
            onClick={() =>
              test.mutate(undefined, {
                onSuccess: () =>
                  onFeedback({
                    tone: 'success',
                    text: 'Bank menerima permintaan access token. Kredensial ini berfungsi.',
                  }),
                onError: (testError) =>
                  onFeedback({ tone: 'danger', text: describeError(testError) }),
              })
            }
          >
            <IconPlugConnected size={18} stroke={1.75} aria-hidden="true" />
            {test.isPending ? 'Menguji...' : 'Uji koneksi'}
          </button>
          <Link className="btn" to="/merchants">
            <IconRefresh size={18} stroke={1.75} aria-hidden="true" />
            Lihat merchant pemakai
          </Link>
        </div>
      </div>
    </Dialog>
  )
}

function InboundUrl({ label, value }: { label: string; value: string }) {
  if (!value) {
    return null
  }
  return (
    <div className="copy-row">
      <span className="fact__label">{label}</span>
      <code className="copy-row__value">{value}</code>
      <CopyButton value={value} label={`Salin ${label.toLowerCase()}`} small />
    </div>
  )
}
